define('threadsThreadDraftView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'backbone',
  'emailAttachmentsView',
  'mailTemplateRenderer',
  'utils/mail_html_utils',
  'modalsAlertView',
  'template!threadsThreadDraftView',
  'entities/threads',
  'entities/email_reply_watches'
], function (
  App,
  Module,
  Marionette,
  _,
  Backbone,
  EmailAttachmentsView,
  MailTemplateRenderer,
  MailHtmlUtils,
  AlertView,
  Tpl
) {
  'use strict';

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'draft-compose-view',

    ui: {
      header: '.draft-header',
      to: '.draft-to',
      cc: '.draft-cc',
      subject: '.draft-subject',
      message: '.draft-message',
      btnSend: '.btn-send',
      btnSave: '.btn-save',
      btnDiscard: '.btn-discard',
      status: '.draft-status',
      notifyOnReply: '.notify-reply-check'
    },

    regions: {
      attachmentsRegion: '.attachments-region'
    },

    events: {
      'click @ui.header': 'onToggleCollapse',
      'click @ui.btnSend': 'onSend',
      'click @ui.btnSave': 'onSave',
      'click @ui.btnDiscard': 'onDiscard',
      'input @ui.to': 'onContentChange',
      'input @ui.cc': 'onContentChange',
      'input @ui.subject': 'onContentChange',
      'input @ui.message': 'onContentChange',
      'keyup @ui.message': 'onContentChange'
    },

    initialize: function () {
      this.saveTimeout = null;
      this.dirty = false;
      // Files the operator attaches to the draft. Included on save (persisted
      // into the Gmail draft) and on send.
      this.attachmentsCollection = new Backbone.Collection();
      // Collapsible mode (opt-in by the host, e.g. the Sales Inbox embed):
      // the composer starts minimised to its header bar so the conversation
      // gets the room, and expands when the bar is clicked. The standalone
      // Threads page doesn't pass the flag, so the draft stays open.
      this.collapsible = !!this.options.collapsible;
      this.collapsed = this.collapsible && this.options.collapsed !== false;
    },

    onRender: function () {
      this.applyCollapsed();
    },

    onShow: function () {
      this.attachmentsView = new EmailAttachmentsView({
        collection: this.attachmentsCollection
      });
      this.getRegion('attachmentsRegion').show(this.attachmentsView);
      // Adding/removing a file is an edit: it must auto-save into the draft and
      // gate Send until every attachment has finished loading.
      this.attachmentsView.on('change', _.bind(this.onAttachmentsChange, this));
    },

    onAttachmentsChange: function () {
      this.refreshSendState();
      this.onContentChange();
    },

    // Load a mail template into this draft composer. Mirrors the reply
    // composer's loadTemplate so a status-change / picked template works whether
    // the thread currently shows the reply composer or a saved draft: render the
    // template to HTML, drop it into the contenteditable body + subject, attach
    // the same files, and mark the draft dirty so it auto-saves.
    loadTemplate: function (emailOptions) {
      var self = this;
      emailOptions = emailOptions || {};

      // Surface the composer so the loaded template is visible.
      if (this.collapsible && this.collapsed) {
        this.collapsed = false;
        this.applyCollapsed();
      }

      MailTemplateRenderer.render(emailOptions).done(function (rendered) {
        if (self.isDestroyed) { return; }
        self.ui.message.html(rendered && rendered.body ? rendered.body : '');
        // Unlike the reply composer, the draft has an editable subject field.
        if (rendered && rendered.subject) {
          self.ui.subject.val(rendered.subject);
        }
        // Persist the template into the Gmail draft (auto-save) and refocus.
        self.onContentChange();
        self.ui.message.focus();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        AlertView.prototype.open('', 'Could not load the mail template for this status.');
      });

      // Attach the same files the popup would (proforma / proofsheet PDFs); the
      // shared EmailAttachmentsView loads each url -> base64.
      _.each(emailOptions.attachments || [], function (att) {
        self.attachmentsCollection.add(att);
      });
      this.refreshSendState();
    },

    // Disable Send while any attachment is still loading or errored.
    refreshSendState: function () {
      if (!this.ui.btnSend || !this.ui.btnSend.length) { return; }
      var pending = this.attachmentsCollection.some(function (a) {
        return a.get('error') || !a.get('loaded');
      });
      this.ui.btnSend.prop('disabled', !!pending).text(pending ? 'Loading attachments…' : 'Send');
    },

    // Reflect the collapsed/collapsible state onto the root element; CSS
    // (draft/view.scss) hides the fields/body/actions while collapsed and
    // turns the header into a clickable bar with a chevron.
    applyCollapsed: function () {
      this.$el.toggleClass('draft-collapsible', this.collapsible);
      this.$el.toggleClass('draft-collapsed', this.collapsible && this.collapsed);
    },

    onToggleCollapse: function () {
      if (!this.collapsible) { return; }
      this.collapsed = !this.collapsed;
      this.applyCollapsed();
      // Focus the body when the operator opens the composer to write.
      if (!this.collapsed && this.ui.message && this.ui.message.length) {
        this.ui.message.focus();
      }
    },

    serializeData: function () {
      var data = {};
      if (this.model) {
        // Never pre-fill the reply's To/Cc with our own mailbox ("from")
        // address — replying to ourselves loops the mail back into the inbox.
        // Also drop blanks and case-insensitive duplicates. Mirrors the reply
        // composer's collectTo/collectCc.
        var ownAddress = ((this.options.mailbox && this.options.mailbox.email) || '').toLowerCase();
        var joinAddrs = function (list) {
          var seen = {};
          var out = [];
          _.each(list || [], function (addr) {
            var email = (addr && addr.address) || '';
            var key = email.toLowerCase();
            if (!email || key === ownAddress || seen[key]) { return; }
            seen[key] = true;
            out.push(email);
          });
          return out.join(', ');
        };

        data.toAddresses = joinAddrs(this.model.get('to'));
        data.ccAddresses = joinAddrs(this.model.get('cc'));

        data.subject = this.model.get('subject') || '';

        // Use HTML content to preserve formatting
        data.content = this.model.get('content') || this.model.get('contentText') || '';

        data.hasDraftId = !!this.model.get('draftId');
        // The composer is always editable now: with a draftId we save+send
        // through the draft, without one we send the frontend content directly.
        data.statusText = '';
      }
      return data;
    },

    onContentChange: function () {
      this.dirty = true;

      // Without a backend draft there is nothing to auto-save into — the
      // content is sent directly on Send — so skip the save scheduling.
      if (!this.model.get('draftId')) { return; }

      this.ui.status.text('Unsaved changes');

      // Auto-save after 3 seconds of inactivity
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(_.bind(this.onSave, this), 3000);
    },

    // Same line-break normalisation as the reply composer (onSend /
    // draftPayload), extended with the <div> blocks a plain contenteditable
    // inserts on Enter (Chrome). Without it, a mail finished in this composer
    // went out with raw <p>/<div> blocks and rendered with different line
    // spacing than one sent through the reply composer.
    _normalizedMessage: function () {
      var html = (this.ui.message.html() || '')
        // A blank line is <div><br></div>; drop the inner <br> first so the
        // block conversion below yields exactly one empty line, not two.
        .replace(/<div><br\s*\/?><\/div>/gi, '<div></div>')
        .replace(/<div>/gi, '<br>')
        .replace(/<\/div>/gi, '');
      return MailHtmlUtils.normalize(html)
        // When the browser wraps the very first line in a block too, the
        // conversion leaves a spurious leading break.
        .replace(/^<br>/i, '');
    },

    // Current frontend content, shaped for both the draft (update) and the
    // direct (reply) endpoints — neither change should ever send the backend's
    // stored content, only what the operator sees in the composer.
    _payload: function () {
      var attachments = this.attachmentsCollection.map(function (attachment) {
        return {
          base64: attachment.get('base64'),
          contentDisposition: attachment.get('content-disposition'),
          contentType: attachment.get('content-type'),
          name: attachment.get('name')
        };
      });
      return {
        to: this.ui.to.val() || null,
        cc: this.ui.cc.val() || null,
        bcc: null,
        message: this._normalizedMessage(),
        attachments: attachments.length ? attachments : null
      };
    },

    // "Notify me of a reply" (EmailReplyWatchService); only sent with the
    // mail itself, never with a draft save.
    _replyWatch: function () {
      return {
        notifyOnReply: this.ui.notifyOnReply.is(':checked'),
        salesInboxItemSecId: this.options.salesInboxItemSecId || null
      };
    },

    onSave: function () {
      clearTimeout(this.saveTimeout);
      if (!this.dirty) return;

      var draftId = this.model.get('draftId');
      if (!draftId) return;

      this.ui.status.text('Saving...');
      this.ui.btnSave.prop('disabled', true);

      App.request('threads:update-draft',
        this.options.mailSecId,
        this.options.threadId,
        draftId,
        this._payload(),
        this.ui.subject.val()
      ).done(_.bind(function (response) {
        this.dirty = false;
        this.ui.status.text('Draft saved');
        this.ui.btnSave.prop('disabled', false);
        if (response && response.draftId) {
          this.model.set('draftId', response.draftId);
        }
      }, this)).fail(_.bind(function () {
        this.ui.status.text('Failed to save');
        this.ui.btnSave.prop('disabled', false);
      }, this));
    },

    onSend: function () {
      var draftId = this.model.get('draftId');

      if (draftId) {
        // Always overwrite the backend draft with the current frontend content
        // before sending — we never send the stored content as-is.
        clearTimeout(this.saveTimeout);
        this.ui.btnSend.prop('disabled', true).text('Saving...');
        this.ui.status.text('Saving before send...');

        App.request('threads:update-draft',
          this.options.mailSecId,
          this.options.threadId,
          draftId,
          this._payload(),
          this.ui.subject.val()
        ).done(_.bind(function (response) {
          this.dirty = false;
          var sendDraftId = (response && response.draftId) ? response.draftId : draftId;
          this._doSend(sendDraftId);
        }, this)).fail(_.bind(function () {
          this.ui.btnSend.prop('disabled', false).text('Send');
          this.ui.status.text('Failed to save before send');
        }, this));
      } else {
        // No backend draft → send the frontend content directly through the
        // thread reply endpoint (same path as the reply composer).
        this._doSendDirect();
      }
    },

    // Send the composer's current content directly, without a backend draft.
    _doSendDirect: function () {
      if (!this.ui.to.val() || !this.ui.message.html()) {
        this.ui.status.text('To and message are required');
        return;
      }

      clearTimeout(this.saveTimeout);
      this.ui.btnSend.prop('disabled', true).text('Sending...');
      this.ui.status.text('Sending...');

      var payload = _.extend(this._payload(), this._replyWatch());
      App.request('threads:reply',
        this.options.mailSecId,
        this.options.threadId,
        payload
      ).done(_.bind(function (response) {
        App.request('emailReplyWatches:warn', payload.notifyOnReply, response);
        this.ui.status.text('Sent!');
        this.trigger('draft:sent');
      }, this)).fail(_.bind(function () {
        this.ui.btnSend.prop('disabled', false).text('Send');
        this.ui.status.text('Failed to send');
      }, this));
    },

    _doSend: function (draftId) {
      this.ui.btnSend.prop('disabled', true).text('Sending...');
      this.ui.status.text('Sending...');

      var replyWatch = this._replyWatch();
      App.request('threads:send-draft',
        this.options.mailSecId,
        this.options.threadId,
        draftId,
        replyWatch
      ).done(_.bind(function (response) {
        App.request('emailReplyWatches:warn', replyWatch.notifyOnReply, response);
        this.ui.status.text('Sent!');
        this.trigger('draft:sent');
      }, this)).fail(_.bind(function () {
        this.ui.btnSend.prop('disabled', false).text('Send');
        this.ui.status.text('Failed to send');
      }, this));
    },

    onDiscard: function () {
      var draftId = this.model.get('draftId');
      if (!draftId) return;

      clearTimeout(this.saveTimeout);
      this.ui.btnDiscard.prop('disabled', true).text('Discarding...');

      App.request('threads:discard-draft',
        this.options.mailSecId,
        this.options.threadId,
        draftId
      ).done(_.bind(function () {
        this.trigger('draft:discarded');
      }, this)).fail(_.bind(function () {
        this.ui.btnDiscard.prop('disabled', false).text('Discard');
        this.ui.status.text('Failed to discard');
      }, this));
    },

    onDestroy: function () {
      clearTimeout(this.saveTimeout);
    }
  });
});

